import React, {useEffect, useState} from 'react'
import {Text, Box, Stack, Flex, useToast, Switch, Button} from '@sanity/ui'
import {useOrganizationData} from '../../../../data'
import {Organization} from '@/types/index'
import {TabSectionHeader} from '@/ui/index'
import {useRequiredOrganizationPermissions} from '@/context/index'
import Link from 'next/link'

export function DashboardSettings({org, title}: {org: Organization; title: string}) {
  const {
    data: orgData,
    patchOrganizationData: editOrg,
    patchSuccess,
    patchError: error,
  } = useOrganizationData(org)

  const {push} = useToast()
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    if (patchSuccess) {
      if (successMessage) {
        push({
          status: 'info',
          description: successMessage,
          closable: true,
        })
      }
    } else if (error) {
      push({
        status: 'error',
        title: 'Failed to update dashboard settings',
        description: 'Failed to update dashboard settings',
      })
    }
  }, [patchSuccess, successMessage, error, push])

  const canManage = useRequiredOrganizationPermissions([
    {permissionName: 'sanity.organization', grantName: 'update'},
  ])

  function handleToggleNewDashboard(enabled: boolean) {
    const newStatus = enabled ? 'enabled' : 'disabled'
    editOrg({dashboardStatus: newStatus})
    setSuccessMessage(`New dashboard experience ${newStatus}`)
  }

  const isDashboardEnabled = orgData?.dashboardStatus === 'enabled'

  return (
    <Box>
      <Stack space={5}>
        <TabSectionHeader title={title} />
        <Stack space={5}>
          <Flex>
            <Box flex={1}>
              <Stack space={3}>
                <Flex align="center" gap={3}>
                  <Switch
                    checked={isDashboardEnabled}
                    onChange={(event) => handleToggleNewDashboard(event.currentTarget.checked)}
                    disabled={!canManage}
                  />
                  <Text size={1} weight="semibold">
                    {isDashboardEnabled
                      ? 'Dashboard is enabled for your organization'
                      : 'Enable Dashboard for your organization'}
                  </Text>
                </Flex>
                <Text size={1} muted>
                  {isDashboardEnabled
                    ? "Studios will launch directly within your organization's unified Dashboard view."
                    : "Make the Dashboard the central hub for accessing all your Sanity Studios. Rather than using separate URLs, Studios will launch directly within your organization's unified Dashboard view."}
                </Text>
                <Flex align={'center'} gap={3}>
                  <Button
                    mode="ghost"
                    as={Link}
                    target="_blank"
                    href={`/@${org.id}`}
                    text="Open dashboard"
                  />

                  <Text size={1}>
                    <Link target="_blank" href="/docs/dashboard">
                      Learn more
                    </Link>
                  </Text>
                </Flex>

                {!canManage && (
                  <Text size={1} muted>
                    You do not have permission to change this organization's settings
                  </Text>
                )}
              </Stack>
            </Box>
          </Flex>
        </Stack>
      </Stack>
    </Box>
  )
}
